/**
 * Custom hook encapsulating all inline-editing state and keyboard handling
 * for renaming states and editing transition labels directly on the canvas.
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import { expandRanges } from '../../utils/automataHelpers';

export function useInlineEditing({ onStateRenameInline, onTransitionLabelInline, onStateDelete, onTransitionDelete }) {
  const [editingElement, setEditingElement] = useState(null); // { type: 'state'|'transition', id, text }
  const editingRef = useRef(null);
  const blinkRef = useRef(true);
  const blinkIntervalRef = useRef(null);

  // Start inline editing — text is fully selected so typing replaces it
  const startEditing = useCallback((type, id, initialText) => {
    const el = { type, id, text: initialText, cursorPos: initialText.length, selectionStart: initialText.length > 0 ? 0 : undefined };
    setEditingElement(el);
    editingRef.current = el;
    blinkRef.current = true;
    if (blinkIntervalRef.current) clearInterval(blinkIntervalRef.current);
    blinkIntervalRef.current = setInterval(() => { blinkRef.current = !blinkRef.current; }, 530);
  }, []);

  // Confirm inline edit
  const confirmEditing = useCallback(() => {
    const el = editingRef.current;
    if (!el) return;
    if (el.type === 'state') {
      onStateRenameInline?.(el.id, el.text.trim() || 'q');
    } else if (el.type === 'transition') {
      const raw = el.text.split(',').map(s => s.trim()).filter(s => s.length > 0);
      const symbols = expandRanges(raw);
      onTransitionLabelInline?.(el.id, symbols);
    }
    setEditingElement(null);
    editingRef.current = null;
    if (blinkIntervalRef.current) { clearInterval(blinkIntervalRef.current); blinkIntervalRef.current = null; }
  }, [onStateRenameInline, onTransitionLabelInline]);

  // Cancel inline edit
  const cancelEditing = useCallback(() => {
    setEditingElement(null);
    editingRef.current = null;
    if (blinkIntervalRef.current) { clearInterval(blinkIntervalRef.current); blinkIntervalRef.current = null; }
  }, []);

  // Cleanup blink interval on unmount
  useEffect(() => {
    return () => { if (blinkIntervalRef.current) clearInterval(blinkIntervalRef.current); };
  }, []);

  // Force canvas redraw during blink for cursor animation
  const [blinkTick, setBlinkTick] = useState(0);
  useEffect(() => {
    if (!editingElement) return;
    const interval = setInterval(() => setBlinkTick(t => t + 1), 530);
    return () => clearInterval(interval);
  }, [editingElement]);

  // Keyboard handler for inline editing
  useEffect(() => {
    const hasSelection = (el) => el.selectionStart !== undefined && el.selectionStart !== el.cursorPos;
    const clearSelection = (el) => { el.selectionStart = undefined; };
    const deleteSelection = (el) => {
      const start = Math.min(el.selectionStart, el.cursorPos);
      const end = Math.max(el.selectionStart, el.cursorPos);
      el.text = el.text.slice(0, start) + el.text.slice(end);
      el.cursorPos = start;
      clearSelection(el);
    };

    const handleKeyDown = (e) => {
      if (!editingRef.current) return;
      const el = editingRef.current;

      if (e.key === 'Enter') {
        e.preventDefault();
        confirmEditing();
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        cancelEditing();
        return;
      }
      if (e.key === 'Backspace') {
        e.preventDefault();
        if (hasSelection(el)) {
          deleteSelection(el);
        } else if (el.cursorPos > 0) {
          el.text = el.text.slice(0, el.cursorPos - 1) + el.text.slice(el.cursorPos);
          el.cursorPos--;
        } else if (el.text.length === 0) {
          // Empty text + Backspace -> delete the element
          const deleteType = el.type;
          const deleteId = el.id;
          cancelEditing();
          if (deleteType === 'state') onStateDelete?.(deleteId);
          else if (deleteType === 'transition') onTransitionDelete?.(deleteId);
          return;
        }
        editingRef.current = { ...el };
        setEditingElement({ ...el });
        return;
      }
      if (e.key === 'Delete') {
        e.preventDefault();
        if (hasSelection(el)) {
          deleteSelection(el);
        } else if (el.cursorPos < el.text.length) {
          el.text = el.text.slice(0, el.cursorPos) + el.text.slice(el.cursorPos + 1);
        } else if (el.text.length === 0) {
          // Empty text + Delete -> delete the element
          const deleteType = el.type;
          const deleteId = el.id;
          cancelEditing();
          if (deleteType === 'state') onStateDelete?.(deleteId);
          else if (deleteType === 'transition') onTransitionDelete?.(deleteId);
          return;
        }
        editingRef.current = { ...el };
        setEditingElement({ ...el });
        return;
      }
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (hasSelection(el)) {
          el.cursorPos = Math.min(el.selectionStart, el.cursorPos);
          clearSelection(el);
        } else if (el.cursorPos > 0) {
          el.cursorPos--;
        }
        editingRef.current = { ...el };
        setEditingElement({ ...el });
        return;
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (hasSelection(el)) {
          el.cursorPos = Math.max(el.selectionStart, el.cursorPos);
          clearSelection(el);
        } else if (el.cursorPos < el.text.length) {
          el.cursorPos++;
        }
        editingRef.current = { ...el };
        setEditingElement({ ...el });
        return;
      }
      // Select all
      if (e.key === 'a' && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        el.selectionStart = 0;
        el.cursorPos = el.text.length;
        editingRef.current = { ...el };
        setEditingElement({ ...el });
        return;
      }
      // Printable character
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        if (hasSelection(el)) {
          deleteSelection(el);
        }
        el.text = el.text.slice(0, el.cursorPos) + e.key + el.text.slice(el.cursorPos);
        el.cursorPos++;
        // Special character shortcuts: \l -> lambda, \e -> epsilon, \0 -> empty set
        if (el.cursorPos >= 2) {
          const lastTwo = el.text.slice(el.cursorPos - 2, el.cursorPos);
          if (lastTwo === '\\l') {
            el.text = el.text.slice(0, el.cursorPos - 2) + '\u03BB' + el.text.slice(el.cursorPos);
            el.cursorPos--;
          } else if (lastTwo === '\\e') {
            el.text = el.text.slice(0, el.cursorPos - 2) + '\u03B5' + el.text.slice(el.cursorPos);
            el.cursorPos--;
          } else if (lastTwo === '\\0') {
            el.text = el.text.slice(0, el.cursorPos - 2) + '\u2205' + el.text.slice(el.cursorPos);
            el.cursorPos--;
          }
        }
        editingRef.current = { ...el };
        setEditingElement({ ...el });
        blinkRef.current = true;
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [confirmEditing, cancelEditing, onStateDelete, onTransitionDelete]);

  return { editingElement, editingRef, blinkRef, blinkTick, startEditing, confirmEditing, cancelEditing };
}
